import React, { useState, useEffect } from 'react';
import MyRaceDetail from './MyRaceDetail';
import LoadingScreen from './LoadingScreen';
import ProfileDropdown from './ProfileDropdown';
import { formatDate, calculateCountdown } from './utils';
import api from './api';
import useIsMobile from './hooks/useIsMobile';

const MyRaces = ({ onBack, raceReadyMode, onToggleRaceReady, user, onLogout }) => {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRace, setSelectedRace] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [raceOverlaps, setRaceOverlaps] = useState({});
  const [expandedOverlap, setExpandedOverlap] = useState(null);
  const [reflectionNeeded, setReflectionNeeded] = useState([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (user) {
      fetchMyRaces();
      fetchOverlaps();
      fetchReflectionNeeded();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchOverlaps = async () => {
    try {
      const res = await api.get('/follows/race-overlaps');
      const map = {};
      for (const item of res.data) {
        map[item.race.id] = item.runners;
      }
      setRaceOverlaps(map);
    } catch {
      // Silently fail - overlaps are supplementary
    }
  };

  const fetchReflectionNeeded = async () => {
    try {
      const res = await api.get('/strategy/reflection-needed');
      setReflectionNeeded(res.data);
    } catch {
      // Silently fail
    }
  };

  const fetchMyRaces = async () => {
    try {
      const res = await api.get('/registrations');
      const userRaces = res.data
        .filter(reg => reg.race)
        .map(reg => ({
          ...reg.race,
          registrationId: reg.id,
          communityId: reg.race.id,
          end_date: reg.race.date,
          name: reg.race.name,
          location: reg.race.location,
          image_url: reg.race.image,
        }));

      setRaces(userRaces);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching races:', error);
      setRaces([]);
      setLoading(false);
    }
  };

  if (selectedRace) {
    return (
      <MyRaceDetail
        race={selectedRace}
        onBack={() => {
          setSelectedRace(null);
          fetchMyRaces();
        }}
        raceReadyMode={raceReadyMode}
        user={user}
        onLogout={onLogout}
        onToggleRaceReady={onToggleRaceReady}
      />
    );
  }

  if (loading) {
    return <LoadingScreen message="Loading your races..." />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0B10',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: raceReadyMode ? '#0B0B10' : 'linear-gradient(135deg, #0B0B10 0%, #1E1E28 100%)',
        padding: isMobile ? '1rem' : '1rem 2rem',
        borderBottom: '1px solid rgba(255, 138, 61, 0.15)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Left side - Logo or Back button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {raceReadyMode ? (
              <img
                src={`${process.env.PUBLIC_URL}/runrora-logo.png`}
                alt="Runrora"
                style={{
                  height: '40px',
                  width: 'auto'
                }}
              />
            ) : (
              <>
                <button
                  onClick={onBack}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#FF8A3D',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    padding: '0.5rem'
                  }}
                >
                  ←
                </button>
                <h1 style={{
                  fontSize: '2rem',
                  fontFamily: 'Instrument Serif, serif',
                  color: '#FFFFFF',
                  margin: 0
                }}>
                  My Races
                </h1>
              </>
            )}
          </div>

          {/* Center - Title with Race-Ready badge (only in Race-Ready Mode) */}
          {raceReadyMode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h1 style={{
                fontSize: '1.5rem',
                fontFamily: 'Instrument Serif, serif',
                color: '#FFFFFF',
                margin: 0
              }}>
                My Races
              </h1>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.75rem',
                background: 'rgba(255, 138, 61, 0.15)',
                border: '1px solid rgba(255, 138, 61, 0.3)',
                borderRadius: '20px'
              }}>
                <span style={{ fontSize: '0.85rem' }}>🏁</span>
                <span style={{ color: '#FF8A3D', fontSize: '0.75rem', fontWeight: 600 }}>
                  Race-Ready Mode
                </span>
              </div>
            </div>
          )}

          {/* Right side - Profile dropdown (only in Race-Ready Mode) */}
          {raceReadyMode && (
            <ProfileDropdown
              currentUserId={user?.id}
              userName={user?.name}
              raceReadyMode={raceReadyMode}
              onToggleRaceReady={onToggleRaceReady}
              onLogout={onLogout}
              showProfileDropdown={showProfileDropdown}
              setShowProfileDropdown={setShowProfileDropdown}
            />
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '1rem' : '2rem'
      }}>
        {/* Training Insights for upcoming races */}
        {races.filter(race => !calculateCountdown(race.end_date).isPast).length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            {races
              .filter(race => !calculateCountdown(race.end_date).isPast)
              .map(race => {
                const countdown = calculateCountdown(race.end_date);
                if (countdown.days > 90) return null;
                let message, icon, borderColor;
                if (countdown.days <= 7) {
                  icon = '🏁';
                  message = `Race week! ${race.name} is in ${countdown.days} ${countdown.days === 1 ? 'day' : 'days'}. Trust your training — focus on rest and nutrition.`;
                  borderColor = '#FF8A3D';
                } else if (countdown.days <= 21) {
                  icon = '📊';
                  message = `${countdown.days} days until ${race.name}. This is the time to fine-tune your pace strategy and lock in your fuel plan.`;
                  borderColor = 'rgba(100, 180, 255, 0.5)';
                } else if (countdown.days <= 60) {
                  icon = '🏃';
                  message = `${countdown.days} days until ${race.name}. Keep building your base — consistency beats intensity right now.`;
                  borderColor = 'rgba(100, 180, 255, 0.3)';
                } else {
                  return null;
                }
                return (
                  <div
                    key={`insight-${race.id}`}
                    style={{
                      background: countdown.days <= 7
                        ? 'linear-gradient(135deg, rgba(255, 138, 61, 0.08) 0%, rgba(255, 138, 61, 0.02) 100%)'
                        : 'linear-gradient(135deg, rgba(100, 180, 255, 0.08) 0%, rgba(100, 180, 255, 0.02) 100%)',
                      border: `1px solid ${borderColor}`,
                      borderRadius: '12px',
                      padding: '1rem 1.25rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.1rem' }}>{icon}</span>
                      <span style={{
                        color: '#FFFFFF',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                      }}>
                        {race.name}
                      </span>
                      <span style={{
                        marginLeft: 'auto',
                        color: countdown.days <= 7 ? '#FF8A3D' : '#9ca3af',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                      }}>
                        {countdown.days}d
                      </span>
                    </div>
                    <p style={{
                      color: '#9ca3af',
                      fontSize: '0.85rem',
                      margin: 0,
                      lineHeight: 1.5,
                    }}>
                      {message}
                    </p>
                  </div>
                );
              })
              .filter(Boolean)}
          </div>
        )}

        {races.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#9ca3af'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏃</div>
            <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>No races yet</h3>
            <p>Register for a race to see it here!</p>
            <button
              onClick={onBack}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                background: '#FF8A3D',
                border: 'none',
                color: '#0B0B10',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Browse Races
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Upcoming Races */}
            {races.filter(race => !calculateCountdown(race.end_date).isPast).length > 0 && (
              <>
                <h2 style={{ color: '#FFFFFF', fontSize: '1.25rem', margin: '0 0 0.5rem 0' }}>
                  Upcoming Races
                </h2>
                {races
                  .filter(race => !calculateCountdown(race.end_date).isPast)
                  .map((race) => {
                    const countdown = calculateCountdown(race.end_date);
                    return (
                      <div
                        key={race.registrationId}
                        onClick={() => setSelectedRace(race)}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 138, 61, 0.2)',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#FF8A3D';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255, 138, 61, 0.2)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {/* Race Image */}
                        <div style={{
                          width: isMobile ? '100%' : '200px',
                          minHeight: isMobile ? '120px' : '150px',
                          background: `url(${race.image_url}) center/cover`,
                          flexShrink: 0
                        }} />

                        {/* Race Info */}
                        <div style={{
                          flex: 1,
                          padding: isMobile ? '1rem' : '1.5rem',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          justifyContent: 'space-between',
                          alignItems: isMobile ? 'flex-start' : 'center',
                          gap: isMobile ? '0.75rem' : '0'
                        }}>
                          <div>
                            <h3 style={{
                              color: '#FFFFFF',
                              fontSize: '1.25rem',
                              margin: '0 0 0.5rem 0',
                              fontWeight: 600
                            }}>
                              {race.name}
                            </h3>
                            <p style={{
                              color: '#FF8A3D',
                              fontSize: '0.9rem',
                              margin: '0 0 0.25rem 0'
                            }}>
                              📍 {race.location}
                            </p>
                            <p style={{
                              color: '#9ca3af',
                              fontSize: '0.9rem',
                              margin: 0
                            }}>
                              🗓 {formatDate(race.end_date, 'medium')}
                            </p>
                            {raceOverlaps[race.id] && raceOverlaps[race.id].length > 0 && (
                              <div style={{ marginTop: '0.5rem' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedOverlap(expandedOverlap === race.id ? null : race.id);
                                  }}
                                  style={{
                                    background: 'rgba(255, 138, 61, 0.12)',
                                    border: '1px solid rgba(255, 138, 61, 0.25)',
                                    color: '#FF8A3D',
                                    padding: '0.3rem 0.75rem',
                                    borderRadius: '14px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  👥 {raceOverlaps[race.id].length} {raceOverlaps[race.id].length === 1 ? 'runner' : 'runners'} you follow
                                </button>
                                {expandedOverlap === race.id && (
                                  <div style={{
                                    marginTop: '0.5rem',
                                    display: 'flex',
                                    gap: '0.5rem',
                                    flexWrap: 'wrap',
                                  }}>
                                    {raceOverlaps[race.id].map(runner => {
                                      const name = runner.first_name
                                        ? `${runner.first_name} ${runner.last_name || ''}`.trim()
                                        : runner.name;
                                      return (
                                        <span key={runner.id} style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '0.35rem',
                                          background: 'rgba(255, 255, 255, 0.05)',
                                          padding: '0.25rem 0.6rem',
                                          borderRadius: '12px',
                                          fontSize: '0.8rem',
                                          color: '#FFFFFF',
                                        }}>
                                          {runner.profile_photo ? (
                                            <img src={runner.profile_photo} alt={`${runner.first_name || runner.name || 'Runner'} avatar`} style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
                                          ) : (
                                            <span style={{
                                              width: '20px',
                                              height: '20px',
                                              borderRadius: '50%',
                                              background: 'rgba(255, 138, 61, 0.2)',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              fontSize: '0.6rem',
                                              color: '#FF8A3D',
                                              fontWeight: 700,
                                            }}>
                                              {(name || 'R')[0].toUpperCase()}
                                            </span>
                                          )}
                                          {name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Countdown */}
                          <div style={{
                            textAlign: 'center',
                            padding: '1rem 1.5rem',
                            background: countdown.days <= 7
                              ? 'rgba(255, 138, 61, 0.2)'
                              : 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '12px',
                            minWidth: '100px'
                          }}>
                            <div style={{
                              fontSize: '2.5rem',
                              fontWeight: 700,
                              color: countdown.days <= 7 ? '#FF8A3D' : '#FFFFFF',
                              lineHeight: 1
                            }}>
                              {countdown.days}
                            </div>
                            <div style={{
                              color: '#9ca3af',
                              fontSize: '0.85rem',
                              marginTop: '0.25rem'
                            }}>
                              {countdown.days === 1 ? 'day' : 'days'} to go
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </>
            )}

            {/* Reflection Prompts */}
            {reflectionNeeded.length > 0 && (
              <>
                <h2 style={{ color: '#FF8A3D', fontSize: '1.25rem', margin: '2rem 0 0.5rem 0' }}>
                  How did it go?
                </h2>
                {reflectionNeeded.map(race => (
                  <div
                    key={`reflect-${race.id}`}
                    onClick={() => {
                      const raceData = races.find(r => r.id === race.id);
                      if (raceData) setSelectedRace(raceData);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 138, 61, 0.08) 0%, rgba(255, 138, 61, 0.02) 100%)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '16px',
                      padding: isMobile ? '1rem' : '1.25rem 1.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#FF8A3D';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255, 138, 61, 0.3)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(255, 138, 61, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      flexShrink: 0,
                    }}>
                      📝
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#FFFFFF', fontSize: '1rem', fontWeight: 600 }}>
                        How was {race.name}?
                      </div>
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                        Share what worked, what didn't, and your highlight
                      </div>
                    </div>
                    <div style={{
                      color: '#FF8A3D',
                      fontSize: '1.25rem',
                      flexShrink: 0,
                    }}>
                      →
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Past Races */}
            {races.filter(race => calculateCountdown(race.end_date).isPast).length > 0 && (
              <>
                <h2 style={{
                  color: '#9ca3af',
                  fontSize: '1.25rem',
                  margin: '2rem 0 0.5rem 0'
                }}>
                  Completed Races
                </h2>
                {races
                  .filter(race => calculateCountdown(race.end_date).isPast)
                  .map((race) => {
                    return (
                      <div
                        key={race.registrationId}
                        onClick={() => setSelectedRace(race)}
                        style={{
                          background: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid rgba(255, 138, 61, 0.1)',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          opacity: 0.7
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.borderColor = 'rgba(255, 138, 61, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '0.7';
                          e.currentTarget.style.borderColor = 'rgba(255, 138, 61, 0.1)';
                        }}
                      >
                        {/* Race Image */}
                        <div style={{
                          width: isMobile ? '100%' : '150px',
                          minHeight: isMobile ? '100px' : '120px',
                          background: `url(${race.image_url}) center/cover`,
                          flexShrink: 0,
                          filter: 'grayscale(50%)'
                        }} />

                        {/* Race Info */}
                        <div style={{
                          flex: 1,
                          padding: isMobile ? '1rem' : '1.25rem',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          justifyContent: 'space-between',
                          alignItems: isMobile ? 'flex-start' : 'center',
                          gap: isMobile ? '0.5rem' : '0'
                        }}>
                          <div>
                            <h3 style={{
                              color: '#FFFFFF',
                              fontSize: '1.1rem',
                              margin: '0 0 0.5rem 0',
                              fontWeight: 600
                            }}>
                              {race.name}
                            </h3>
                            <p style={{
                              color: '#9ca3af',
                              fontSize: '0.85rem',
                              margin: 0
                            }}>
                              {formatDate(race.end_date, 'medium')}
                            </p>
                          </div>

                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            background: 'rgba(34, 197, 94, 0.1)',
                            border: '1px solid rgba(34, 197, 94, 0.2)',
                            borderRadius: '12px',
                          }}>
                            <span style={{ fontSize: '1rem' }}>✅</span>
                            <span style={{
                              color: '#22c55e',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                            }}>Completed</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRaces;
